/**
 * Question World Module - Paragraph Component
 * Long text answer question type
 */

import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Input, Card, Button, Space, Image, Popover, Progress, Modal } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';

import {
    setAnsweredQuestionCount,
    setOfflineSavedAnswers,
    setNoOfSubmittedResponse
} from '../../../../../../stateManager/reducers/studentAuthSlice';
import {
    patchFileRequest,
    signedUrl as signedUrlRequest
} from '../../../../../../stateManager/reducers/questionSlice';
import { bucket_url, bucket_url_old, shouldUseKatex } from '../../helpers';
import { MathInput } from '../common';
import '../../styles/Paragraph.css';
import PdfPlaceholder from '../../assets/pdf_placeholder.png';
import PictureGrid from './PictureGrid';
import QuestionsImage from './QuestionsImage';
import QuestionTitle from './QuestionTitle';
import { Answer } from '../../helpers/Answer';
import ImagePreview from './ImagePreview';

const { Text } = Typography;
const { TextArea } = Input;

let answer = null;

const ParagraphQuestion = props => {
    const {
        question,
        questionIndex,
        groupId,
        examId,
        isResult,
        isAssessment,
        examMeta
    } = props;
    const dispatch = useDispatch();
    const [selectedOption, setSelectedOption] = useState('');
    const [fileList, setFileList] = useState([]);
    const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
    const [offline, setOffline] = useState(false);
    const [clicked, setClicked] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [submitError, setSubmitError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [answerSubmitted, setAnswerSubmitted] = useState(false);

    const hoverContent = <div>You are offline!</div>;

    const hide = () => {
        setClicked(false);
        setHovered(false);
    };

    const handleHoverChange = (visible) => {
        setHovered(visible);
        setClicked(false);
    };

    const handleClickChange = (visible) => {
        setHovered(false);
        setClicked(visible);
    };

    useEffect(() => {
        const handleOffline = () => setOffline(true);
        const handleOnline = () => setOffline(false);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, []);

    useEffect(() => {
        const localData = localStorage.getItem('localSavedAnswers');
        if (localData) {
            const localSavedAnswers = JSON.parse(localData);

            if (localSavedAnswers[examId]) {
                if (localSavedAnswers[examId]?.[question._id]) {
                    const savedAnswer = localSavedAnswers[examId][question._id];
                    dispatch(setAnsweredQuestionCount({ id: question._id, flag: true }));
                    setAnswerSubmitted(true);
                    if (savedAnswer?.offline === true) {
                        dispatch(setOfflineSavedAnswers(question._id));
                    }
                } else {
                    setAnswerSubmitted(false);
                }
                const ans = localSavedAnswers[examId]?.[question._id]?.answer?.text;
                const files =
                    localSavedAnswers[examId]?.[question._id]?.answer?.fileList || [];
                setSelectedOption(ans);
                setFileList(files);
            }
        }
    }, [question, dispatch, examId]);

    const saveToLocalStorage = (isOffline) => {
        setIsSubmittingAnswer(false);
        if (isOffline) dispatch(setOfflineSavedAnswers(question._id));
        const localData = localStorage.getItem('localSavedAnswers');
        let isPrevious = false;
        if (localData) {
            const localSavedAnswers = JSON.parse(localData);
            isPrevious = !!localSavedAnswers[examId];
        }
        if (isPrevious) {
            const localSavedAnswers = JSON.parse(localData);
            localSavedAnswers[examId][question._id] = {
                answer: { text: selectedOption, fileList: fileList },
                offline: isOffline,
                timestamp: Date.now()
            };
            localStorage.setItem('localSavedAnswers', JSON.stringify(localSavedAnswers));
        } else {
            const localSavedAnswers = {
                [examId]: {
                    [question._id]: {
                        answer: { text: selectedOption, fileList: fileList },
                        offline: isOffline,
                        timestamp: Date.now()
                    }
                }
            };
            localStorage.setItem('localSavedAnswers', JSON.stringify(localSavedAnswers));
        }
    };

    const handleSubmitAnswer = async ({ attachments }) => {
        saveToLocalStorage(false);

        const data = {
            questionId: question._id,
            answer: selectedOption,
            timestamp: Date.now(),
            extra: attachments
        };

        try {
            if (answer) {
                answer.cancel();
            }
            answer = new Answer(dispatch, groupId, examId, data, question, false, () => setSelectedOption(undefined), !examMeta?.examId?.isPracticeExam);
            const response = await answer.handleSubmit();
            if (response?.response?.status === '200') {
                dispatch(setNoOfSubmittedResponse({
                    id: question._id,
                    count: 1
                }));
            }
            setIsSubmittingAnswer(false);
        } catch (err) {
            console.log('ANSWER => answer submit err: ', err);
            saveToLocalStorage(true);
        }
    };

    const handleFileUpload = async () => {
        if (fileList.length <= 0 && !selectedOption) {
            setErrorMessage('Please provide an answer or upload a file');
            setSubmitError(true);
            return;
        }
        setIsSubmittingAnswer(true);
        dispatch(setAnsweredQuestionCount(question?._id));
        try {
            const attachments = [];
            for (let i = 0; i < fileList.length; i++) {
                const item = fileList[i];
                const signedUrlRes = await dispatch(signedUrlRequest(item.type));
                const patchRes = await dispatch(
                    patchFileRequest({
                        signedUrl: signedUrlRes?.payload?.data?.signedUrl,
                        file: item.originFileObj
                    })
                );

                if (patchRes?.error) {
                    setErrorMessage(`Error uploading file ${item.name}`);
                    setSubmitError(true);
                    setIsSubmittingAnswer(false);
                    return;
                }

                const { key } = signedUrlRes?.payload?.data;
                attachments.push({ name: item.name, key, type: item.type });

                const progress = ((i + 1) / fileList.length) * 100;
                setUploadProgress(Number(parseInt(progress)));
            }

            await handleSubmitAnswer({ attachments });
            setAnswerSubmitted(true);
        } catch (error) {
            console.log('Online image upload error:', error);
            setIsSubmittingAnswer(false);
        }
    };

    const onFileChange = ({ fileList: fl }) => {
        const files = fl?.fileList || fl;
        setFileList(files);
    };

    const uploadButton = (
        <div>
            {isSubmittingAnswer ? <LoadingOutlined /> : <PlusOutlined />}
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    );

    const questionTitleText = () => {
        let title = '';
        if (isResult) {
            title = question?.questionId?.title || '';
        } else if (isAssessment) {
            title = question?.question?.title || '';
        } else {
            title = question?.title || '';
        }
        return shouldUseKatex(title) > -1 ? <MathInput mathItem={title} /> : title;
    };

    return (
        <Card className='paragraph-que' bordered={false} style={{ width: '100%' }}>
            <QuestionTitle
                title={questionTitleText()}
                marks={isResult ? question?.marks || '0' : question?.point}
                questionIndex={questionIndex}
            />

            {!isAssessment &&
                !isResult &&
                (question?.image || question?.file) &&
                (question?.image?.length > 0 || question?.file?.length > 0) && (
                    <QuestionsImage question={question} />
                )}

            {!isAssessment &&
                isResult &&
                (question?.questionId?.image || question?.questionId?.file) &&
                (question?.questionId?.image?.length > 0 ||
                    question?.questionId?.file?.length > 0) && (
                    <Row style={{ marginTop: 30, marginBottom: 30 }}>
                        <Col xs={23} md={23} offset={1}>
                            <Space>
                                {question?.questionId?.image &&
                                    question?.questionId?.image.map((item, idx) => (
                                        <Image key={idx} placeholder src={bucket_url + item} width={'100%'} />
                                    ))}
                                {!!question &&
                                    question?.questionId?.file &&
                                    question.questionId?.file.map((item, idx) => (
                                        <a key={idx} href={bucket_url + item} target='_blank' rel="noreferrer">
                                            <Image
                                                alt='PDF File'
                                                placeholder={<Image src={PdfPlaceholder} />}
                                                src={bucket_url + item}
                                                fallback={PdfPlaceholder}
                                                width={'100%'}
                                                style={{ objectFit: 'contain' }}
                                            />
                                        </a>
                                    ))}
                            </Space>
                        </Col>
                    </Row>
                )}
            {isAssessment &&
                (question?.question?.image || question?.question?.file) &&
                (question?.question?.image?.length > 0 ||
                    question?.question?.file?.length > 0) && (
                    <Row style={{ marginTop: 30, marginBottom: 30 }}>
                        <Col xs={23} md={23} offset={1}>
                            <Space>
                                {question?.question?.image &&
                                    question?.question?.image.map((item, idx) => (
                                        <Image key={idx} placeholder src={(item?.startsWith('user') ? bucket_url_old : bucket_url) + item} width={'100%'} />
                                    ))}
                                {!!question &&
                                    question?.question?.file &&
                                    question.question?.file.map((item, idx) => (
                                        <a key={idx} href={(item?.startsWith('user') ? bucket_url_old : bucket_url) + item} target='_blank' rel="noreferrer">
                                            <Image
                                                alt='PDF File'
                                                placeholder={<Image src={PdfPlaceholder} />}
                                                src={(item?.startsWith('user') ? bucket_url_old : bucket_url) + item}
                                                fallback={PdfPlaceholder}
                                                width={'100%'}
                                                style={{ objectFit: 'contain' }}
                                            />
                                        </a>
                                    ))}
                            </Space>
                        </Col>
                    </Row>
                )}
            <Row>
                <Col xs={23} md={20} offset={1}>
                    {(isAssessment || isResult) && (
                        <React.Fragment>
                            <Text>Answer: </Text>
                            <Text>{question?.answer}</Text>
                        </React.Fragment>
                    )}

                    {!isAssessment && !isResult && (
                        <TextArea
                            value={selectedOption}
                            disabled={question.result}
                            onChange={e => {
                                setSelectedOption(e.target.value);
                            }}
                            placeholder='Your Answer'
                            autoSize={{ minRows: 3, maxRows: 5 }}
                        />
                    )}
                </Col>
            </Row>
            {!isResult && !isAssessment && (
                <Row style={{ marginTop: 30, marginBottom: 30 }}>
                    <Col xs={23} md={20} offset={1}>
                        <PictureGrid
                            listType='picture-card'
                            onFileChange={onFileChange}
                            fileList={fileList}
                            multiple={true}
                            disabled={offline}
                        >
                            <Popover
                                style={{ width: 500 }}
                                content={hoverContent}
                                trigger="hover"
                                open={hovered && offline}
                                onOpenChange={handleHoverChange}
                            >
                                <Popover
                                    content={
                                        <div>
                                            {hoverContent}
                                            <a href="#close" onClick={hide}>Close</a>
                                        </div>
                                    }
                                    trigger="click"
                                    open={clicked && offline}
                                    onOpenChange={handleClickChange}
                                >
                                    {uploadButton}
                                </Popover>
                            </Popover>
                        </PictureGrid>
                    </Col>
                </Row>
            )}
            {(isAssessment || isResult) &&
                question?.extra &&
                question?.extra?.length > 0 && (
                    <Row style={{ marginTop: 30, marginBottom: 30 }}>
                        <Col xs={23} md={23} offset={1}>
                            <Space>
                                {question.extra.map((item, idx) =>
                                    item.type.includes('image') ? (
                                        <Image
                                            key={idx}
                                            placeholder
                                            src={((item.evaluatedKey ? item.evaluatedKey : item?.key)?.startsWith('user') ? bucket_url_old : bucket_url) + (item.evaluatedKey ? item.evaluatedKey : item?.key)}
                                            width={'120px'}
                                            height={'120px'}
                                            style={{ objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <a key={idx} href={`${(item.key?.startsWith('user') ? bucket_url_old : bucket_url)}${item.key}`} target='_blank' rel="noreferrer">
                                            <img
                                                alt="PDF File"
                                                src="/pdf.png"
                                                width={'120px'}
                                                height={'120px'}
                                                style={{ objectFit: 'contain' }} />
                                        </a>
                                    )
                                )}
                            </Space>
                        </Col>
                    </Row>
                )}

            <Row>
                {isResult &&
                    (question?.questionId?.explanation ||
                        question?.questionId?.explanationExt) && (
                        <Col xs={24} md={24} style={{ marginTop: 10 }}>
                            <Card className='explanation-card' bordered>
                                <Text strong>Explanation</Text>

                                <p style={{ marginBottom: 0 }}>
                                    {question?.questionId?.explanation &&
                                        shouldUseKatex(question?.questionId?.explanation) > -1 ? (
                                        <MathInput mathItem={question?.questionId?.explanation} />
                                    ) : (
                                        question?.questionId?.explanation
                                    )}
                                </p>
                                <Row>
                                    <Col xs={24} md={24}>
                                        <ImagePreview question={question} />
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    )}

                {isResult && question?.notes && (
                    <Col xs={24} md={24} style={{ marginTop: 10 }}>
                        <Card className='notes-card' bordered>
                            <Text strong>Notes</Text>

                            <p style={{ marginBottom: 0 }}>
                                {question?.notes && shouldUseKatex(question?.notes) > -1 ? (
                                    <MathInput mathItem={question?.notes} />
                                ) : (
                                    question?.notes
                                )}
                            </p>
                        </Card>
                    </Col>
                )}
            </Row>

            {!isResult && !isAssessment && isSubmittingAnswer && (
                <Row>
                    <Progress percent={uploadProgress} status="active" />
                </Row>
            )}

            {!isResult && !isAssessment && (
                <Row style={{ marginTop: 30 }}>
                    <Col xs={23} md={20} offset={1}>
                        <Button
                            loading={isSubmittingAnswer}
                            onClick={() => {
                                if (window.confirm("Are you sure you want to submit? Once submitted, it can't be changed.")) {
                                    handleFileUpload();
                                }
                            }}
                            disabled={answerSubmitted}
                        >
                            {answerSubmitted ? 'Answer Submitted' : 'Submit Answer'}
                        </Button>
                    </Col>
                </Row>
            )}

            <Modal
                open={submitError}
                title="Submit Answer Error"
                footer={null}
                closable={true}
                onCancel={() => setSubmitError(false)}
            >
                <h2>{`Question No ${questionIndex + 1}`}</h2>
                <p>{errorMessage}</p>
            </Modal>
        </Card>
    );
};

export default ParagraphQuestion;

